import { ArrowRight } from 'lucide-react';
import { cn } from '../../shared/utils/cn';
import type { PortalRole } from '../authMetadata';

/**
 * Registrations on the legacy portal are closed (CollabsMap transition, step 1).
 * New accounts are created on the new portals; existing users still sign in here.
 *
 * Destination is role-aware per the CollabsMap auth topology (canonical singular
 * subdomains):
 *   - creators            → https://creator.videreo.com
 *   - partners/orgs (brand + agency) → https://partner.videreo.com
 *
 * When the signup intent is unknown on a given path (e.g. a bare OAuth callback),
 * callers should default to 'creator'.
 *
 * NOTE: copy is intentionally neutral and pending a CMO copy pass.
 */
function registrationsClosedUrl(role: PortalRole | undefined): string {
  return role === 'org' ? 'https://partner.videreo.com' : 'https://creator.videreo.com';
}

const NEW_PORTAL_NAME = (role: PortalRole | undefined) =>
  role === 'org' ? 'partner.videreo.com' : 'creator.videreo.com';

interface RegistrationsClosedNoticeProps {
  role?: PortalRole;
  className?: string;
}

/**
 * Inline note rendered in place of the removed "create account" affordance on the
 * sign-in surfaces (and the unauthenticated invite path).
 */
export function RegistrationsClosedNotice({ role, className }: RegistrationsClosedNoticeProps) {
  const url = registrationsClosedUrl(role);
  return (
    <div
      className={cn(
        'rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left text-sm text-blue-900',
        className,
      )}
    >
      <p>
        <span className="font-semibold">Registrations are closed.</span>{' '}
        <span className="text-blue-800">
          We&apos;ve moved to CollabsMap. New accounts are created on the new portal — if you already
          have a Videreo account you can keep signing in here.
        </span>
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1.5 inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-blue-950"
      >
        Go to {NEW_PORTAL_NAME(role)}
        <ArrowRight className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

interface RegistrationsClosedScreenProps {
  role?: PortalRole;
  heading?: string;
}

/**
 * Full-page "registrations closed" screen for paths where there is no surrounding
 * sign-in form to fall back to (rejected new OAuth users, the gated /org/new route).
 */
export function RegistrationsClosedScreen({
  role,
  heading = 'Registrations are closed',
}: RegistrationsClosedScreenProps) {
  const url = registrationsClosedUrl(role);
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center shadow-sm">
        <h1 className="font-satoshi text-xl font-bold text-gray-900 dark:text-gray-100">{heading}</h1>
        <p className="mt-3 font-satoshi text-sm text-gray-600 dark:text-gray-300">
          We&apos;ve moved to CollabsMap. New accounts are created on the new portal. If you already
          have a Videreo account, you can still sign in here.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'mt-6 inline-flex w-full items-center justify-center gap-2 h-12 rounded-lg',
            'bg-[var(--color-primary-orange)] hover:opacity-90',
            'text-white font-satoshi font-bold text-sm uppercase transition-all duration-200',
          )}
        >
          Go to {NEW_PORTAL_NAME(role)}
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}
